import Cookies from 'js-cookie';
import { getMyChainChildren } from 'clients/ChainClient';
import { isValid } from 'clients/Clients';
import { ACCESS_TOKEN, ACCESS_TOKEN_LONGLIVE, GENERAL_DOMAIN } from 'sysconfig';

const CHAIN_SESSION_STORAGE_KEY = 'chain_session_state';
export const CHAIN_PRESENCE_MINIMIZED_KEY = 'chain_presence_minimized';
const isProduction = process.env.NODE_ENV === 'production';
const RETRY_DELAYS_MS = [500, 1000, 2000];

const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export interface ChildStoreItem {
  customerID: number;
  accountID: number;
  code: string;
  name: string;
  avatarURL?: string;
  phone?: string;
  address?: string;
  status?: string;
  isActive?: boolean;
}

export interface ChainResponseData {
  chainCode: string;
  totalStores: number;
  parentCustomerID: number;
  parentAccountID: number;
  parentName: string;
  parentAvatarURL?: string;
  children: ChildStoreItem[];
}

export function isChildStoreActive(child: ChildStoreItem): boolean {
  if (typeof child?.isActive === 'boolean') {
    return child.isActive;
  }
  const st = (child?.status || '').toUpperCase().trim();
  if (!st) return true;
  return st === 'ACTIVE';
}

export const sortChildrenActiveFirst = (children: ChildStoreItem[] = []): ChildStoreItem[] => [
  ...children.filter((child) => child.isActive ?? isChildStoreActive(child)),
  ...children.filter((child) => !(child.isActive ?? isChildStoreActive(child))),
];

export function getChildStoreStatusLabel(child?: ChildStoreItem): string {
  const st = (child?.status || '').toUpperCase().trim();
  switch (st) {
    case 'ACTIVE':
      return 'Đang hoạt động';
    case 'INACTIVE':
      return 'Chưa kích hoạt';
    case 'DELETED':
      return 'Đã xóa';
    case 'BLOCK':
      return 'Đã chặn';
    case 'GUEST':
      return 'Dùng thử';
    case 'DRAFT':
      return 'Nháp';
    case 'VERIFIED':
      return 'Đã xác thực';
    default:
      return isChildStoreActive(child as ChildStoreItem) ? 'Đang hoạt động' : 'Chưa kích hoạt';
  }
}

export function purgeStoreContextCache(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    sessionStorage.removeItem('cart_data');
    sessionStorage.removeItem(CHAIN_PRESENCE_MINIMIZED_KEY);
  } catch {
    /* ignore storage access error */
  }
}

const getCookieOptions = () => ({
  domain: GENERAL_DOMAIN,
  sameSite: 'Lax' as const,
  ...(isProduction ? { secure: true } : {}),
});

export interface ChainSessionState {
  isImpersonating: boolean;
  parentToken: string;
  parentAccountId: number;
  parentCustomerName: string;
  parentCustomerCode: string;
  activeChildAccountId: number | null;
  activeChildCustomerCode?: string;
  activeChildStoreName?: string;
  activeChildPhone?: string;
  activeChildAddress?: string;
  chainCode: string;
}

export function getChainSessionState(): ChainSessionState | null {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = localStorage.getItem(CHAIN_SESSION_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    return JSON.parse(raw) as ChainSessionState;
  } catch {
    return null;
  }
}

export function saveChainSessionState(state: ChainSessionState): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    localStorage.setItem(CHAIN_SESSION_STORAGE_KEY, JSON.stringify(state));
  } catch {
    /* ignore storage access error */
  }
}

export function clearChainSessionState(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    localStorage.removeItem(CHAIN_SESSION_STORAGE_KEY);
    sessionStorage.removeItem(CHAIN_PRESENCE_MINIMIZED_KEY);
  } catch {
    /* ignore storage access error */
  }
}

/**
 * Returns the persisted chain impersonation state only when an authenticated user session is present.
 */
export function getValidatedChainSessionState(user?: any): ChainSessionState | null {
  if (!user || !user?.accountID) {
    return null;
  }
  return getChainSessionState();
}

/**
 * Fetches the chain hierarchy for the current account with bounded exponential backoff retries.
 */
export async function fetchChainChildrenWithRetry(
  shouldAbort?: () => boolean,
): Promise<ChainResponseData | null> {
  for (let attempt = 0; attempt <= RETRY_DELAYS_MS.length; attempt++) {
    if (shouldAbort?.()) {
      return null;
    }
    try {
      const resp = await getMyChainChildren();
      if (shouldAbort?.()) {
        return null;
      }
      if (isValid(resp) && resp?.data && resp.data.length > 0) {
        return resp.data[0];
      }
      throw new Error(resp?.message || 'Không thể tải danh sách chi nhánh.');
    } catch {
      if (attempt < RETRY_DELAYS_MS.length) {
        await sleep(RETRY_DELAYS_MS[attempt]);
      }
    }
  }
  return null;
}

/**
 * Establishes an impersonation session for a child store and preserves parent state.
 */
export function setChildImpersonationSession(
  childToken: string,
  parentToken: string,
  parentUser: any,
  childStore: any,
): void {
  const cookieOptions = getCookieOptions();
  Cookies.set(ACCESS_TOKEN, childToken, cookieOptions);
  Cookies.set(ACCESS_TOKEN_LONGLIVE, childToken, {
    ...cookieOptions,
    expires: 5000,
  });

  const state: ChainSessionState = {
    isImpersonating: true,
    parentToken,
    parentAccountId: parentUser?.accountID ?? 0,
    parentCustomerName: parentUser?.name ?? '',
    parentCustomerCode: parentUser?.code ?? '',
    activeChildAccountId: childStore?.accountID ?? null,
    activeChildCustomerCode: childStore?.code ?? '',
    activeChildStoreName: childStore?.name ?? '',
    activeChildPhone: childStore?.phone ?? '',
    activeChildAddress: childStore?.address ?? '',
    chainCode: parentUser?.chainCode ?? childStore?.chainCode ?? '',
  };

  saveChainSessionState(state);
  purgeStoreContextCache();
}

/**
 * Restores the parent chain session and terminates child impersonation.
 */
export function restoreParentSession(): void {
  const state = getChainSessionState();
  if (state?.parentToken) {
    const cookieOptions = getCookieOptions();
    Cookies.set(ACCESS_TOKEN, state.parentToken, cookieOptions);
    Cookies.set(ACCESS_TOKEN_LONGLIVE, state.parentToken, {
      ...cookieOptions,
      expires: 5000,
    });
  }
  clearChainSessionState();
  purgeStoreContextCache();
}

export function restoreActiveChildSession(childToken: string): void {
  if (!childToken) {
    return;
  }
  const cookieOptions = getCookieOptions();
  Cookies.set(ACCESS_TOKEN, childToken, cookieOptions);
  Cookies.set(ACCESS_TOKEN_LONGLIVE, childToken, {
    ...cookieOptions,
    expires: 5000,
  });
}
